import {Subscription} from "rxjs";
import {AccValidator} from "./validator";
import {OrgDto} from "../../repositories/dto/org.dto";
import {LoggingService} from "../../services/logger/logging.service";
import {inject} from "@angular/core";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function nonBlank(value: any): boolean {
  if (value !== undefined && value !== null && value !== '') {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    } else if (typeof value === 'number' || typeof value === 'object') {
      return true;
    }
    throw new Error('Value type not handled "' + (typeof value) + '"');
  }
  return false;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function isBlank(value: any): boolean {
  return !nonBlank(value);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function isUuid(value: any): value is string {
  return AccValidator.isUUID(value);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function nonUuid(value: any): boolean {
  return !isUuid(value);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function isNumeric(value: any): value is number {
  return AccValidator.isNumeric(value);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function isTrue(input: any): input is true {
  const value: string = String(input).toLowerCase();
  return input === true || value === 'true' || input === 1 || value === '1' || value === 'y' || value === 'yes' || value === 'on';
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function isFalse(input: any): input is false {
  const value: string = String(input).toLowerCase();
  return input === false || value === 'false' || input === 0 || value === '0' || value === 'n' || value === 'no' || value === 'off';
}

export function isDefined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

export function notDefined<T>(value: T | undefined | null): value is undefined | null {
  return !isDefined(value);
}

// A date without time is held as UTC midnight (DTO parsing, I18nService.toDate) - today is compared in the same form
export function isAfterToday(date: Date | undefined): boolean {
  const now: Date = new Date();
  return isDefined(date) && date.getTime() > Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

export function unsubscribe(sub: Subscription | any[] | number | undefined): void {
  if (isDefined(sub)) {
    if (Array.isArray(sub)) {
      sub.forEach((s) => unsubscribe(s));
    } else if (sub instanceof Subscription) {
      sub.unsubscribe();
    } else {
      clearTimeout(sub);
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function isIterable(obj: any): boolean {
  if (notDefined(obj)) {
    return false;
  }
  return typeof obj[Symbol.iterator] === 'function';
}

export function isOrg(org: OrgDto | undefined): org is OrgDto {
  return isDefined(org) && isUuid(org.id);
}

export function uniqueArray<T>(arr: T[], key: string): T[] {
  // @ts-ignore
  return [...new Map(arr.reverse().map((item) => [item[key], item])).values()].reverse();
}

// noinspection JSUnusedGlobalSymbols
export function arraysNonEqual(arr1: any[], arr2: any[]): boolean {
  return !arraysEqual(arr1, arr2);
}

export function arraysEqual(arr1: any[] | undefined, arr2: any[] | undefined): boolean {
  if (notDefined(arr1) && notDefined(arr2)) {
    return true;
  }
  return JSON.stringify(arr1) === JSON.stringify(arr2);
}

export function arraysNonEqualUnordered(arr1: any[], arr2: any[]): boolean {
  return !arraysEqualUnordered(arr1, arr2);
}

export function arraysEqualUnordered(arr1: any[], arr2: any[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  arr1 = arr1.slice().sort();
  arr2 = arr2.slice().sort();
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }
  return true;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function serializeDto(data: any): string {
  return JSON.stringify(data, (_key, value) => {
    if (value instanceof Map) {
      return Object.fromEntries(value);
    }
    return value;
  });
}

export function deserializeDto(json: string): any {
  return JSON.parse(json, (_key, value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return new Map(Object.entries(value));
    }
    return value;
  });
}

export function blobToJson<T>(blob: Blob): Promise<T> {
  return new Promise((resolve) => blob.text().then((txt) => resolve(JSON.parse(txt) as T)));
}

export function objectKeyAndMethods(object: object): string[] {
  if (object) {
    return [...Object.keys(object),
      ...Object.getOwnPropertyNames(Object.getPrototypeOf(object)).filter((k) => k !== 'constructor')];
  }
  return [];
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function safeSplit(value: any, delimiter: string, log?: LoggingService): string[] {
  if (isDefined(value)) {
    if (typeof value === 'string') {
      return value.split(delimiter);
    }
    const logger: LoggingService | undefined = log || inject(LoggingService, {optional: true}) || undefined;
    if (logger) {
      logger.warn('safeSplit: Expected string but got type "{}". Value: {}', [typeof value, JSON.stringify(value)]);
    } else {
      console.warn('safeSplit: Expected string but got type "' + (typeof value) + '". Value: ' + JSON.stringify(value));
    }
  }
  return [];
}
